import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { fmtPrice, FREE_SHIPPING } from "../lib/shop";

export function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal } = useCart();
  const remaining = Math.max(0, FREE_SHIPPING - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-[60] bg-navy/50 backdrop-blur-sm"
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-skytint px-6 py-5">
              <h2 className="font-display text-lg font-bold text-navy">
                Coșul tău <span className="text-navy">({items.length})</span>
              </h2>
              <button
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-skytint"
                aria-label="Închide coșul"
                data-testid="cart-drawer-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-foam">
                  <ShoppingBag className="h-7 w-7 text-sky-600" />
                </span>
                <p className="font-display text-lg font-bold text-navy">Coșul este gol</p>
                <p className="text-sm text-muted2">Adaugă produse și bucură-te de prospețime supremă.</p>
                <Link
                  to="/produse"
                  onClick={() => setDrawerOpen(false)}
                  className="mt-2 rounded-lg bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy"
                  data-testid="cart-empty-shop-link"
                >
                  Vezi produsele
                </Link>
              </div>
            ) : (
              <>
                <div className="border-b border-skytint px-6 py-4">
                  {remaining > 0 ? (
                    <p className="text-xs font-semibold text-muted2" data-testid="free-shipping-hint">
                      Mai adaugă <span className="text-navy">{fmtPrice(remaining)}</span> pentru livrare gratuită
                    </p>
                  ) : (
                    <p className="text-xs font-semibold text-navy" data-testid="free-shipping-hint">
                      Ai livrare gratuită!
                    </p>
                  )}
                  <div className="mt-2 h-1.5 overflow-hidden rounded-lg bg-skytint">
                    <motion.div
                      className="h-full rounded-lg bg-sky2"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <ul className="flex flex-col gap-5">
                    {items.map((item) => (
                      <li key={item.key || item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
                        <img
                          src={item.image}
                          alt={item.name}
                          className="h-20 w-20 shrink-0 rounded-2xl border border-skytint object-cover"
                        />
                        <div className="flex flex-1 flex-col">
                          <p className="text-sm font-bold leading-snug text-navy">{item.name}</p>
                          <p className="text-xs text-sky-light">{item.volume}</p>
                          {item.note && <p className="mt-0.5 text-xs italic text-muted2">{item.note}</p>}
                          <div className="mt-auto flex items-center justify-between pt-1">
                            <div className="flex items-center gap-2 rounded-full border border-line px-2 py-1">
                              <button onClick={() => updateQty(item.key || item.id, item.qty - 1)} className="text-muted2 hover:text-navy" aria-label="Scade cantitatea" data-testid={`cart-qty-minus-${item.id}`}>
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="min-w-5 text-center text-sm font-bold" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
                              <button onClick={() => updateQty(item.key || item.id, item.qty + 1)} className="text-muted2 hover:text-navy" aria-label="Crește cantitatea" data-testid={`cart-qty-plus-${item.id}`}>
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <span className="text-sm font-extrabold text-navy">{fmtPrice(item.price * item.qty)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.key || item.id)}
                          className="self-start text-line transition-colors hover:text-rose-500"
                          aria-label="Șterge produsul"
                          data-testid={`cart-remove-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-skytint px-6 py-5">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-muted2">Subtotal</span>
                    <span className="font-display text-xl font-extrabold text-navy" data-testid="cart-subtotal">
                      {fmtPrice(subtotal)}
                    </span>
                  </div>
                  <Link
                    to="/checkout"
                    onClick={() => setDrawerOpen(false)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-navy py-4 text-sm font-bold text-white transition-all hover:bg-navy-dark hover:shadow-lg hover:shadow-navy/30"
                    data-testid="cart-checkout-button"
                  >
                    Finalizează comanda <ArrowRight className="h-4 w-4" />
                  </Link>
                  <p className="mt-3 text-center text-[11px] text-sky-light">
                    Plată ramburs la livrare • Fără cont necesar
                  </p>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
